import { PrivateFilePreviewModal } from "@/components/documents/widgets/PrivateFilePreviewModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook";
import { useDocumentDetailRefetcher } from "@/queries/use-documents.query.ts";
import { analyticEvents, analyticProperties } from "@/services/analytics-service.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiDocumentPreview } from "@/types/document.types.ts";
import { ModalProps } from "@skailer-com/uikit";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface EmployeeDocumentPreviewModalProps extends ModalProps {
  file: ApiFile;
  preview?: ApiDocumentPreview;
  documentTitle?: string;
  documentUpdatedAt?: Date;
  documentId?: number;
  analyticsSource?: string;
}

export function EmployeeDocumentPreviewModal({
  file,
  preview,
  documentTitle,
  documentId,
  documentUpdatedAt,
  analyticsSource = "Employee Document Preview Modal",
  ...props
}: EmployeeDocumentPreviewModalProps) {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const refetchDocument = useDocumentDetailRefetcher();
  const refetchPreview = useCallback(
    async () =>
      documentId === undefined ? undefined : (await refetchDocument(documentId))?.preview,
    [documentId, refetchDocument]
  );
  const analytics = useMemo(
    () => ({
      viewed: analyticEvents.documents.viewed,
      downloaded: analyticEvents.documents.downloaded,
      properties: {
        [analyticProperties.id]: documentId,
        [analyticProperties.source]: analyticsSource,
        [analyticProperties.title]: documentTitle || file.name,
      },
    }),
    [documentId, analyticsSource, documentTitle, file.name]
  );
  const dateUpdated =
    `${t("common:created")}: ` + ((documentUpdatedAt && renderDate(documentUpdatedAt)) || "-");
  const fileName = file.name.length < 100 ? file.name : `${file.name.slice(0, 100).trimEnd()}...`;
  return (
    <PrivateFilePreviewModal
      {...props}
      file={file}
      preview={preview}
      refetchPreview={documentId === undefined ? undefined : refetchPreview}
      analytics={analytics}
      caption={`${fileName}  ·  ${dateUpdated}`}
      title={documentTitle || file.name}
    />
  );
}
